import React, { useMemo } from "react";
import Bubble from "../../../components/bubble";
import { Mode } from "../types";
import { useChatView } from "../hooks/presentation/useChatView";
import { useTutorChatStore } from "../store/useTutorChatStore";
import { Message } from "../types/tutor.types";

/**
 * Chat Container
 *
 * 채팅 메시지를 표시하는 컨테이너 컴포넌트
 *
 * 역할:
 * - useChatView 훅으로 채팅 데이터 및 핸들러 연결
 * - useTutorChatStore에서 실시간 스트리밍 메시지 표시
 * - 메시지 렌더링 (사용자 입력 + AI 응답)
 * - 로딩 상태 처리
 */

interface ChatProps {
  mode: Mode;
  groupId: string | null;
}

const ChatContainer: React.FC<ChatProps> = React.memo(({ mode, groupId }) => {
  const {
    chatHistory,
    messages,
    isLoading,
    showWarning,
    warningText,
    isSpeaking,
    currentSpeakingMessageId,
    handleCopy,
    handleLike,
    handleDislike,
    handleRegenerate,
    handleRegenerateStreaming,
    handleTts,
    handleSuggestionClick,
    convertReferences,
  } = useChatView({
    mode,
    groupId,
  });

  // 로딩 상태 계산 (백엔드 히스토리가 없거나 임시 그룹 ID일 때는 로딩 표시하지 않음)
  const isActuallyLoading =
    isLoading &&
    (chatHistory?.length ?? 0) > 0 &&
    !groupId?.startsWith("temp-");
  // Zustand 스토어에서 실시간 스트리밍 메시지 가져오기
  const { history: streamingHistory } = useTutorChatStore();

  // 백엔드에 저장되지 않은 메시지 필터링 (스트리밍 중 + 완료되었지만 아직 저장 안됨)
  // 현재 그룹의 메시지만 표시 (임시 그룹 ID도 포함)
  const streamingMessages = useMemo(() => {
    const backendMessageIds = new Set(chatHistory.map((msg) => msg.id));
    const filteredMessages = streamingHistory.filter((msg) => {
      // 백엔드에 이미 저장된 메시지는 제외
      if (backendMessageIds.has(msg.id)) return false;

      // 현재 그룹의 메시지 표시 (타입 변환 고려)
      if (
        msg.group_id === groupId ||
        msg.group_id === String(groupId) ||
        String(msg.group_id) === groupId
      )
        return true;

      // groupId가 없는 경우 (첫 질문): 임시 그룹 ID (temp-로 시작) 메시지 표시
      if (!groupId && msg.group_id?.toString().startsWith("temp-")) return true;

      // 추가: 스트리밍 중인 메시지는 모두 표시 (안전장치)
      // 이렇게 하면 그룹 ID 변경 중에도 메시지가 사라지지 않음
      const isStreaming = msg.is_loading === true;
      if (isStreaming) return true;

      return false;
    });

    return filteredMessages;
  }, [streamingHistory, chatHistory, groupId]);

  // 메시지가 하나도 없으면 null 반환 (부모 컴포넌트에서 empty state 처리)
  if (!chatHistory.length && streamingMessages.length === 0) {
    return null;
  }

  /**
   * 스트리밍 메시지 렌더링
   * - 사용자 메시지와 AI 메시지를 쌍으로 렌더링
   */
  const renderStreamingMessage = (message: Message, isLastMessage: boolean) => {
    // AI 메시지만 렌더링 (사용자 메시지는 별도 렌더링)
    if (message.sender !== "ai") {
      return null;
    }

    return (
      <div key={message.id} className="flex justify-start px-4">
        <Bubble
          sender="agent-tu"
          state={message.is_loading ? "loading" : "completed"}
          content={message.content || ""}
          references={
            message.reference ? convertReferences(message.reference) : undefined
          }
          suggestions={message.recommended_questions}
          showActions={!message.is_loading} // 스트리밍 중에는 actions 숨김
          showSuggestions={
            !message.is_loading && message.recommended_questions?.length > 0
          } // 로딩 완료 & 추천 질문 있을 때만
          onSuggestionClick={handleSuggestionClick}
          // 중단된 메시지(마지막 메시지 & 로딩 완료)에 재시도 버튼 추가
          onRegenerate={
            isLastMessage && !message.is_loading
              ? () => handleRegenerateStreaming(message)
              : undefined
          }
          className="max-w-full"
        />
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col gap-6 w-full max-w-[800px] pb-4">
        {isActuallyLoading ? (
          <div className="flex justify-center items-center p-8">
            <span className="text-gray-500">로딩 중...</span>
          </div>
        ) : (
          <>
            {/* 완료된 메시지 (useChatView) */}
            {messages.map((message, index) => (
              <React.Fragment key={message.id}>
                {/* 사용자 메시지 */}
                <div className="flex justify-end px-4">
                  <Bubble
                    sender="user"
                    state="completed"
                    content={message.input}
                    className="max-w-full"
                  />
                </div>

                {/* AI 응답 */}
                <div className="flex justify-start px-4">
                  <Bubble
                    sender="agent-tu"
                    state="completed"
                    content={message.output}
                    references={message.detailedReferences}
                    suggestions={message.suggestions}
                    showActions={true}
                    showSuggestions={message.suggestions?.length > 0}
                    userEva={message.userEva}
                    onCopy={() => handleCopy(chatHistory[index])}
                    onLike={() => handleLike(chatHistory[index])}
                    onDislike={() => handleDislike(chatHistory[index])}
                    onFeedbackSubmit={message.onFeedbackSubmit}
                    onRegenerate={
                      index === messages.length - 1
                        ? () => handleRegenerate(chatHistory[index])
                        : undefined
                    }
                    onTts={() => handleTts(chatHistory[index])}
                    onSuggestionClick={handleSuggestionClick}
                    isTtsSpeaking={isSpeaking}
                    currentSpeakingMessageId={currentSpeakingMessageId}
                    responseId={message.responseId}
                    className="max-w-full"
                  />
                </div>
              </React.Fragment>
            ))}

            {/* 스트리밍 중인 메시지 (useTutorChatStore) */}
            {streamingMessages.length > 0 && (
              <>
                {/* 사용자 메시지 먼저 표시 */}
                {streamingMessages
                  .filter((msg) => msg.sender === "user")
                  .map((message) => (
                    <div key={message.id} className="flex justify-end px-4">
                      <Bubble
                        sender="user"
                        state="completed"
                        content={message.content}
                        className="max-w-full"
                      />
                    </div>
                  ))}

                {/* AI 메시지 표시 (로딩 또는 완료) */}
                {streamingMessages
                  .filter((msg) => msg.sender === "ai")
                  .map((message, index, aiMessages) =>
                    renderStreamingMessage(
                      message,
                      index === aiMessages.length - 1
                    )
                  )}
              </>
            )}
          </>
        )}
      </div>

      {/* 복사 완료 알림 */}
      {showWarning && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-black/30 pointer-events-none">
          <div className="bg-white rounded-lg shadow-xl p-4 min-w-[200px] pointer-events-auto">
            <div className="font-bold text-sm mb-1">알림</div>
            <div className="text-sm">{warningText}</div>
          </div>
        </div>
      )}
    </>
  );
});

ChatContainer.displayName = "ChatContainer";

export default ChatContainer;
